import type { ComponentProps } from "react";
import { Tabs as UITabs } from "ancestry-ui-react";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";

/**
 * Prop types mirror the real `ancestry-ui-react` Tabs by deriving them directly from
 * it, so the wrapper stays a drop-in replacement and cannot drift from `desc.d.ts`.
 */
export type TabsProps = ComponentProps<typeof UITabs>;

/**
 * Safe boundary wrapper for `ancestry-ui-react`'s `Tabs` (ADR-012 §3,
 * gate-the-mount).
 *
 * The underlying `Tabs` reads `window.ui.tabs` unconditionally in
 * `componentDidMount`, so mounting it before Ancestry's vanilla UI library
 * (`core.js`) has populated `window.ui` throws and surfaces as a `Main` FCI. We gate
 * the mount on the shared, hydration-safe readiness signal: render nothing until
 * `window.ui.tabs` exists, then mount the real component.
 *
 * Tabs are interaction-gated and paint no visible DOM until they initialize, so
 * gating introduces no layout shift at the boundary.
 */
const SafeTabs = (props: TabsProps) => {
    const isLoaded = useUIComponentLoaded("tabs");

    if (!isLoaded) return null;

    return <UITabs {...props} />;
};

/**
 * Re-expose the real compound `Tabs.Nav` / `Tabs.Content` on the wrapper so existing
 * `<Tabs.Nav>` / `<Tabs.Content>` JSX keeps working unchanged, rendering the exact
 * real components. They only ever render as children of the gated `Tabs`, so they
 * never mount before the underlying component does.
 *
 * The members are attached via lazy getters rather than read eagerly at module load:
 * importing this boundary (barrel) must never touch `ancestry-ui-react` before render
 * — the same deferral the `Callout`/`Modal` wrappers get for free by only referencing
 * the library inside their render body. This keeps the boundary importable under
 * partial test mocks.
 */
export const Tabs = SafeTabs as typeof SafeTabs & {
    readonly Nav: (typeof UITabs)["Nav"];
    readonly Content: (typeof UITabs)["Content"];
};

Object.defineProperty(Tabs, "Nav", {
    get: () => UITabs.Nav,
    enumerable: true,
});

Object.defineProperty(Tabs, "Content", {
    get: () => UITabs.Content,
    enumerable: true,
});
